import { useEffect, useState } from "react";

export type CursorPoint = { x: number; y: number };

const ACTIVE_CLASS = "custom-cursor-active";
const MIN_WIDTH_FOR_CUSTOM_CURSOR = 768;

function prefersFinePointer(): boolean {
  return window.matchMedia("(pointer: fine)").matches;
}

export function useCustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [point, setPoint] = useState<CursorPoint>({ x: 0, y: 0 });

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    const syncEnabled = () =>
      setEnabled(mq.matches && window.innerWidth >= MIN_WIDTH_FOR_CUSTOM_CURSOR);
    syncEnabled();
    window.addEventListener("resize", syncEnabled);
    mq.addEventListener("change", syncEnabled);
    return () => {
      window.removeEventListener("resize", syncEnabled);
      mq.removeEventListener("change", syncEnabled);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const root = document.documentElement;
    root.classList.add(ACTIVE_CLASS);

    const onMove = (event: MouseEvent) => {
      setPoint({ x: event.clientX, y: event.clientY });
      setVisible(true);
    };

    const onLeaveWindow = () => setVisible(false);

    const onVisibility = () => {
      if (document.visibilityState !== "visible") {
        setVisible(false);
      }
    };

    window.addEventListener("mousemove", onMove);
    root.addEventListener("mouseleave", onLeaveWindow);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      root.classList.remove(ACTIVE_CLASS);
      window.removeEventListener("mousemove", onMove);
      root.removeEventListener("mouseleave", onLeaveWindow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);

  return { enabled, visible, point };
}

export { prefersFinePointer };
